// src/services/api/sharepointService.ts - Enhanced with URL targeting support

import { SharePointListItem, PaginatedResponse } from '../../types/sharepoint.types';

// NEW: Interface for URL analysis response
interface URLAnalysis {
    url_type: 'site' | 'list' | 'library' | 'folder';
    target_list_id?: string;
    target_library_id?: string;
    target_folder_path?: string;
}

// NEW: Enhanced connection response interface
interface EnhancedConnectionResponse {
    message: string;
    data: any;
    connection_status: 'success' | 'failure';
    tenant: string;
    url_analysis: URLAnalysis;
    resolution_method?: string;
    resolution_info?: any;
}

// ENHANCED: Fetch SharePoint lists with optional targeting
export const fetchLists = async (
    sharepointId: string, 
    sharepoint_url: string, 
    app_id: string, 
    isRefresh?: boolean,
    targetListId?: string  // NEW: Optional targeting parameter
) => {
    let refresh_document_cache = !!isRefresh;
    
    const requestBody: any = {
        sharepoint_id: sharepointId, 
        sharepoint_url, 
        refresh_document_cache, 
        app_id
    };

    // NEW: Add targeting parameter if provided
    if (targetListId) {
        requestBody.target_list_id = targetListId;
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/get-lists`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
    });
    const result = await response.json();

    // Ensure listsData is always an array
    if (response.ok && Array.isArray(result.data)) {
        return result.data;
    } else {
        return []; // Return an empty array if data is not valid
    }
};

// ENHANCED: Fetch SharePoint document libraries with optional targeting
// UPDATED fetchDocumentLibraries function for sharepointService.ts

// ENHANCED: Fetch SharePoint document libraries with optional targeting
export const fetchDocumentLibraries = async (
    sharepointId: string, 
    sharepoint_url: string, 
    app_id: string, 
    isRefresh?: boolean,
    targetLibraryId?: string  // EXISTING: Optional targeting parameter
) => {
    let refresh_document_cache = !!isRefresh;
    
    const requestBody: any = {
        sharepoint_id: sharepointId, 
        sharepoint_url, 
        refresh_document_cache, 
        app_id
    };

    // ADD: Include targeting parameter if provided
    if (targetLibraryId) {
        requestBody.target_library_id = targetLibraryId;
        console.log(`🎯 Fetching document libraries with targeting: ${targetLibraryId}`);
    } else {
        console.log(`📚 Fetching all document libraries (no targeting)`);
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/get-document-libraries`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
    });
    
    const result = await response.json();

    // Log the response for debugging
    if (response.ok && Array.isArray(result.data)) {
        const count = result.data.length;
        console.log(`✅ Successfully fetched ${count} document libraries${targetLibraryId ? ' (targeted)' : ' (all)'}`);
        return result.data;
    } else {
        console.error('❌ Failed to fetch document libraries:', result.message);
        return []; // Return an empty array if data is not valid
    }
};

// NEW: Enhanced connection function that returns URL analysis
export const createSharePointConnection = async (
    siteUrl: string,
    sharePointUser: string,
    siteId?: string
): Promise<EnhancedConnectionResponse> => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/create-connection`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            site_url: siteUrl,
            sharepoint_user: sharePointUser,
            site_id: siteId
        }),
    });

    const result = await response.json();
    
    // Ensure URL analysis is always present
    if (!result.url_analysis) {
        result.url_analysis = {
            url_type: 'site',
            target_list_id: null,
            target_library_id: null,
            target_folder_path: null
        };
    }

    return result as EnhancedConnectionResponse;
};

// EXISTING: Fetch items from a specific SharePoint list (original method - kept for backward compatibility)
export const fetchListItems = async (sharepointId: string, listId: string, app_id: string, limit?: number): Promise<SharePointListItem[]> => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/get-list-items`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            sharepoint_id: sharepointId, 
            list_id: listId, 
            limit: limit || 100
        }),
    });
    const result = await response.json();

    // Ensure list items data is always an array
    if (response.ok && Array.isArray(result.data)) {
        return result.data;
    } else {
        console.error('Failed to fetch list items:', result.message);
        return []; // Return an empty array if data is not valid
    }
};

// EXISTING: Fetch list items using simple endpoint with dynamic filtering (no pagination)
export const fetchListItemsSimple = async (
    sharepointId: string, 
    listId: string, 
    filters?: Record<string, any>
): Promise<SharePointListItem[]> => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/get-list-items-simple`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                sharepoint_id: sharepointId, 
                list_id: listId,
                filters: filters || null
            }),
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();

        // Return just the fields data (simple format)
        if (Array.isArray(result.data)) {
            return result.data;
        } else {
            console.error('Failed to fetch simple list items - invalid data format:', result.message);
            return [];
        }
    } catch (error) {
        console.error('Failed to fetch simple list items:', error);
        return [];
    }
};

// UPDATED: Fetch list items with nextLink-based pagination (primary method for drill-down)
export const fetchListItemsWithNextLink = async (
    sharepointId: string, 
    listId: string, 
    filters?: Record<string, any>,
    pageSize: number = 10, // Changed default from 50 to 10
    nextLink?: string
): Promise<PaginatedResponse<SharePointListItem>> => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/get-list-items-simple-paginated`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                sharepoint_id: sharepointId, 
                list_id: listId,
                filters: filters || null,
                top: pageSize,
                next_link: nextLink || null // Use actual nextLink instead of calculating pages
            }),
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();

        // Build standardized nextLink-based pagination response
        const paginatedResponse: PaginatedResponse<SharePointListItem> = {
            items: Array.isArray(result.items) ? result.items : [],
            nextLink: result.nextLink || undefined,
            prevLink: undefined, // Backend doesn't provide prevLink (we'll handle this with navigation history)
            pageSize: pageSize,
            hasMore: result.has_more || !!result.nextLink,
            count: result.count || result.items?.length || 0,
            filters_applied: result.filters_applied || 0
        };

        return paginatedResponse;
        
    } catch (error) {
        console.error('Failed to fetch list items with nextLink:', error);
        return {
            items: [],
            nextLink: undefined,
            prevLink: undefined,
            pageSize: pageSize,
            hasMore: false,
            count: 0,
            filters_applied: 0
        };
    }
};

 
export const fetchDriveItems = async (
    sharepointId: string, 
    driveId: string, 
    app_id: string, 
    limit?: number,
    target_folder_path?: string  // NEW: Optional folder targeting parameter
): Promise<SharePointListItem[]> => {
    try {
        const requestBody: any = {
            sharepoint_id: sharepointId, 
            drive_id: driveId, 
            limit: limit || 1000
        };

        // NEW: Add folder targeting parameter if provided
        if (target_folder_path) {
            requestBody.target_folder_path = target_folder_path;
            console.log(`🎯 Fetching drive items with folder targeting: ${target_folder_path}`);
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/get-drive-items`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        
        // Handle double-nested response format
        if (result.data) {
            // Check if data is nested (result.data.data) or flat (result.data)
            const actualData = result.data?.data || result.data;
            
            if (Array.isArray(actualData)) {
                console.log(`✅ Successfully fetched ${actualData.length} drive items${target_folder_path ? ` from folder: ${target_folder_path}` : ''}`);
                return actualData;
            }
        }
        
        console.error('Failed to fetch drive items - invalid data format:', result.message);
        return [];
        
    } catch (error) {
        console.error('Failed to fetch drive items:', error);
        if (target_folder_path) {
            console.error(`Folder targeting failed for path: ${target_folder_path}`);
        }
        return [];
    }
};

// EXISTING: Get SharePoint site information
export const fetchSiteInfo = async (sharepointId: string, sharepoint_url: string, app_id: string, isRefresh?: boolean) => {
    let refresh_document_cache = !!isRefresh;
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/get-site-info`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sharepoint_id: sharepointId, sharepoint_url, refresh_document_cache, app_id }),
    });
    const result = await response.json();

    // Return site info or empty object
    if (response.ok && result.data) {
        return result.data;
    } else {
        return {}; // Return an empty object if data is not valid
    }
};

// EXISTING: Get SharePoint permissions for the user
export const fetchSitePermissions = async (sharepointId: string, sharepoint_url: string, app_id: string) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/get-permissions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sharepoint_id: sharepointId, sharepoint_url, app_id }),
    });
    const result = await response.json();

    // Return permissions or empty array
    if (response.ok && Array.isArray(result.data)) {
        return result.data;
    } else {
        return []; // Return an empty array if data is not valid
    }
};

interface SharePointIdentifier {
    sharepoint_id: string;
    sharepoint_url?: string;
}

// EXISTING: Get users with access to SharePoint sites
export const getSharePointUsers = async (sharepointIdentifiers: SharePointIdentifier[]) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/get-users`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sharepoint_sites: sharepointIdentifiers }),
    });
    const result = await response.json();

    // Ensure usersData is always an array
    if (response.ok && Array.isArray(result.data)) {
        return result.data;
    } else {
        return []; // Return an empty array if data is not valid
    }
};

// NEW: Search function with nextLink-based pagination
export const searchSharePointItems = async (
    sharepointId: string,
    listId: string,
    searchTerm: string,
    pageSize: number = 10, // Changed default from 50 to 10
    nextLink?: string,
    searchFields?: string[]
): Promise<PaginatedResponse<SharePointListItem>> => {
    try {
        // Build search filters
        const searchFilters: Record<string, any> = {};
        
        if (searchTerm.trim()) {
            if (searchFields && searchFields.length > 0) {
                // Search in specific fields
                searchFields.forEach(field => {
                    searchFilters[`${field}_contains`] = searchTerm;
                });
            } else {
                // Default search in common fields
                searchFilters['Title_contains'] = searchTerm;
            }
        }
        
        // Use the nextLink-based fetch with search filters
        return await fetchListItemsWithNextLink(
            sharepointId,
            listId,
            searchFilters,
            pageSize,
            nextLink
        );
        
    } catch (error) {
        console.error('Failed to search SharePoint items:', error);
        return {
            items: [],
            nextLink: undefined,
            prevLink: undefined,
            pageSize: pageSize,
            hasMore: false,
            count: 0,
            filters_applied: 1
        };
    }
};

// NEW: Get list schema for dynamic filtering
export const fetchListSchema = async (sharepointId: string, listId: string) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sharepoint/get-list-schema`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                sharepoint_id: sharepointId, 
                list_id: listId
            }),
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        return result.data || {};
        
    } catch (error) {
        console.error('Failed to fetch list schema:', error);
        return {};
    }
};

// UPDATED: Export constants for nextLink-based pagination
export const PAGE_SIZES = [10, 25, 50, 100] as const; // Changed order to start with 10
export const DEFAULT_PAGE_SIZE = 10; // Changed from 50 to 10
export const MIN_PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 100;

// Export error types for better error handling
export class SharePointServiceError extends Error {
    constructor(
        message: string, 
        public statusCode?: number, 
        public endpoint?: string
    ) {
        super(message);
        this.name = 'SharePointServiceError';
    }
}

// NEW: Navigation history utility functions
export const NavigationHistoryUtils = {
    /**
     * Add a nextLink to navigation history
     */
    addToHistory: (history: string[], nextLink: string): string[] => {
        // Avoid duplicates - if the nextLink is already the last item, don't add it again
        if (history.length > 0 && history[history.length - 1] === nextLink) {
            return history;
        }
        return [...history, nextLink];
    },

    /**
     * Get the previous nextLink from history for back navigation
     */
    getPreviousLink: (history: string[], currentIndex: number): string | undefined => {
        if (currentIndex > 0 && history[currentIndex - 1]) {
            return history[currentIndex - 1];
        }
        return undefined;
    },

    /**
     * Get the next nextLink from history for forward navigation
     */
    getNextLink: (history: string[], currentIndex: number): string | undefined => {
        if (currentIndex < history.length - 1 && history[currentIndex + 1]) {
            return history[currentIndex + 1];
        }
        return undefined;
    },

    /**
     * Check if can navigate backwards
     */
    canGoBack: (currentIndex: number): boolean => {
        return currentIndex > 0;
    },

    /**
     * Check if can navigate forwards
     */
    canGoForward: (history: string[], currentIndex: number): boolean => {
        return currentIndex < history.length - 1;
    },

    /**
     * Reset history (used when starting fresh or applying new filters)
     */
    resetHistory: (): { history: string[], index: number } => {
        return { history: [], index: -1 };
    }
};

// NEW: Helper function to create initial pagination state
export const createInitialPaginationState = (pageSize: number = DEFAULT_PAGE_SIZE) => {
    return {
        pageSize,
        hasNextPage: false,
        hasPrevPage: false,
        nextLink: undefined,
        prevLink: undefined,
        itemCount: 0,
        canGoBack: false,
        canGoForward: false
    };
};

// NEW: Helper function to convert backend response to pagination info
export const convertToPaginationInfo = (
    response: PaginatedResponse<SharePointListItem>, 
    navigationHistory: string[], 
    currentHistoryIndex: number
): import('../../types/sharepoint.types').PaginationInfo => {
    return {
        pageSize: response.pageSize,
        hasNextPage: response.hasMore,
        hasPrevPage: NavigationHistoryUtils.canGoBack(currentHistoryIndex),
        nextLink: response.nextLink,
        prevLink: NavigationHistoryUtils.getPreviousLink(navigationHistory, currentHistoryIndex),
        itemCount: response.count,
        canGoBack: NavigationHistoryUtils.canGoBack(currentHistoryIndex),
        canGoForward: NavigationHistoryUtils.canGoForward(navigationHistory, currentHistoryIndex) || response.hasMore
    };
};

// NEW: Utility functions for URL targeting
export const URLTargetingUtils = {
    /**
     * Determine if URL analysis indicates targeting
     */
    isTargeted: (urlAnalysis: URLAnalysis): boolean => {
        return urlAnalysis.url_type !== 'site';
    },

    /**
     * Check if should fetch lists based on URL analysis
     */
    shouldFetchLists: (urlAnalysis: URLAnalysis): boolean => {
        return urlAnalysis.url_type === 'site' || urlAnalysis.url_type === 'list';
    },

    /**
     * Check if should fetch document libraries based on URL analysis
     */
    shouldFetchLibraries: (urlAnalysis: URLAnalysis): boolean => {
        return urlAnalysis.url_type === 'site' || urlAnalysis.url_type === 'library' || urlAnalysis.url_type === 'folder';
    },

    /**
     * Get target list ID from URL analysis
     */
    getTargetListId: (urlAnalysis: URLAnalysis): string | undefined => {
        return urlAnalysis.url_type === 'list' ? urlAnalysis.target_list_id : undefined;
    },

    /**
     * Get target library ID from URL analysis
     */
    getTargetLibraryId: (urlAnalysis: URLAnalysis): string | undefined => {
        return (urlAnalysis.url_type === 'library' || urlAnalysis.url_type === 'folder') 
            ? urlAnalysis.target_library_id 
            : undefined;
    }
};

// COMPATIBILITY: Export the main function with the old name for backward compatibility
export const fetchListItemsSimplePaginated = fetchListItemsWithNextLink;

// NEW: Export types for external use
export type { URLAnalysis, EnhancedConnectionResponse };